import { Project, VersionStatus } from 'contensis-core-api';
import { IContensisContext } from '~/models/IContensisContext';
import { SourceCms, TargetCms } from '~/models/Operations';
import { SearchQueryOptions } from '~/models/SearchQuery';
import ContensisManagementApi from '~/providers/ContensisManagementApi';
import ApiKeyService from '~/services/ApiKeyService';
import AssetsService from '~/services/AssetsService';
import BlocksService from '~/services/BlocksService';
import EntriesService from '~/services/EntriesService';
import ModelsService from '~/services/ModelsService';
import NodesService from '~/services/NodesService';
import ProxiesService from '~/services/ProxiesService';
import RenderersService from '~/services/RenderersService';
import RolesService from '~/services/RolesService';
import TagsService from '~/services/TagsService';
import WebhookSubscriptionService from '~/services/WebhookSubscriptionService';
import WorkflowsService from '~/services/WorkflowsService';
import { TransformGuidFactory } from '~/transformations/transformGuids';
import ContentTypeHelper from '~/util/content-type/ContentTypeHelper';
import { parseZenqlVersionStatus } from '~/util/EntryHelper';
import { parseAlias } from '~/util/tryparse';

class ContensisRepository {
  static assetTypes = ['image', 'video', 'pdf', 'word', 'excel'];

  alias: string;
  apiKeys: ApiKeyService;
  assets!: AssetsService;
  blocks!: BlocksService;
  cms: SourceCms | TargetCms;
  /** The context gathered from content modeling for content migration operations */
  context: IContensisContext;
  entries!: EntriesService;
  // TODO: retire entryIds and replace with a dynamic mapping function fed by entries.raw
  // DONTDO: this would probably be slow to traverse large data sets,
  // keeping an "index" of entryIds will be quicker to process
  entryIds: [string, string, string, string, string][] = []; // contentTypeId, entryId, entryTitle, entryUri, filePath
  existingAssetIds: [string, string, string][] = []; // sourceId, existingId, entryTitle
  generateGuid: (hash: string, nodePath?: string) => string;
  models!: ModelsService;
  nodes!: NodesService;
  project: Project;
  projectId: string;
  proxies!: ProxiesService;
  query: SearchQueryOptions;
  renderers!: RenderersService;
  repo: ContensisManagementApi; // TODO: rename to .api?
  roles: RolesService;
  tags!: TagsService;
  transformContentTypes: { [contentTypeId: string]: string };
  /** A magic function that transforms a guid in a deterministic way
   * so that references to entries and between entry links are maintained
   * when the content has been migrated with transformGuids flag enabled
   * When the transformGuids flag is not enabled this function will
   * simply return the original guid
   */
  transformGuid: (guid: string, nodePath?: string) => string;
  logGuid: TransformGuidFactory['logId'];
  versionStatus: VersionStatus;
  webhooks!: WebhookSubscriptionService;
  workflows: WorkflowsService;
  zenQL: string;

  constructor(
    cms: SourceCms | TargetCms,
    projectId: string,
    repo: ContensisManagementApi,
    query: SearchQueryOptions = {},
    zenQL?: string,
    transformGuids = true,
    transformContentTypes: { [contentTypeId: string]: string } = {}
  ) {
    this.alias = parseAlias(cms?.url) || 'unknown';
    const guidTransformer = new TransformGuidFactory(transformGuids);

    this.apiKeys = new ApiKeyService(this);
    this.project = {
      id: projectId,
      name: projectId,
      description: '',
      primaryLanguage: '',
      supportedLanguages: [],
      deliverySysExclusions: [],
    };
    this.projectId = projectId;
    this.cms = cms;
    this.context = new ContentTypeHelper([], [], []) as IContensisContext;
    this.generateGuid = guidTransformer.generate(this.alias, projectId);
    this.query = query;
    this.repo = repo;
    this.roles = new RolesService(this);
    this.transformContentTypes = transformContentTypes;
    this.transformGuid = guidTransformer.create(this.alias, projectId);
    this.logGuid = guidTransformer.logId;
    this.workflows = new WorkflowsService(this);
    this.zenQL =
      zenQL && !zenQL.includes('sys.versionStatus')
        ? `(${zenQL}) AND sys.versionStatus = ${query.versionStatus || `published`}`
        : zenQL || '';
    this.versionStatus =
      query?.versionStatus ||
      parseZenqlVersionStatus(this.zenQL) ||
      'published';
  }
}

export default ContensisRepository;
